import { api, uploadProgress } from "@/api/client";
import { saveBlobResponse } from "@/lib/download";

export type CheckStatus = "none" | "in_progress" | "completed";

export interface OrderCard {
  ref: string;
  number: string;
  date: string;
  /** Дата отгрузки заказа — по ней оператор и выбирает, что собирать. */
  ship_date: string;
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string;
  kontragent_name: string;
  amount: number;
  qty: number;
  state_ref: string;
  state_name: string;
  posted: boolean;
  check_status: CheckStatus;
  /** Комментарий заказа из 1С: менеджеры пишут туда условия отгрузки, и на
   *  складе это единственное место, где они видны. */
  comment: string;
}

export interface OrderListResponse {
  items: OrderCard[];
  has_more: boolean;
}

export interface RefName {
  ref: string;
  name: string;
}

/** ЧТО собираем. Вид нельзя вывести из ссылки заказа — его называет экран. */
export type OrderKind = "customer" | "transfer";

/** Вид из строки (URL, localStorage). Неизвестное — продажа: так раздел вёл
 * себя до появления перемещений, и другого разумного умолчания у экрана нет.
 * ⚠️ Разбор ОДИН на фронт: копии молча расходятся, а ошибаются они в сторону
 * «показали не тот список», что выглядит как «перемещений нет». */
export function parseKind(value: string | null | undefined): OrderKind {
  return value === "transfer" ? "transfer" : "customer";
}

/** Хвост `?kind=` для ссылок внутри проверки. Пусто у продажи — её адреса не
 * меняются, а вкладку склада не закрывают сутками.
 * ⚠️ Вид обязан ехать в КАЖДОМ переходе между экранами проверки: экраны
 * создают сессию по ссылке заказа, и потерянный вид уводит их искать
 * перемещение среди заказов покупателя — то есть в 404. */
export function kindSuffix(kind: OrderKind): string {
  return kind === "transfer" ? "?kind=transfer" : "";
}

export interface OrderListParams {
  q?: string;
  organization_ref?: string;
  state_ref?: string;
  check_status?: string;
  /** Дата отгрузки `yyyy-mm-dd`: только заказы с отгрузкой в этот день. */
  ship_date?: string;
  kind?: OrderKind;
  skip?: number;
}

export async function listOrders(params: OrderListParams): Promise<OrderListResponse> {
  const { data } = await api.get<OrderListResponse>("/order-check/orders", {
    params: {
      q: params.q || undefined,
      organization_ref: params.organization_ref || undefined,
      state_ref: params.state_ref || undefined,
      check_status: params.check_status || undefined,
      ship_date: params.ship_date || undefined,
      kind: params.kind || undefined,
      skip: params.skip || undefined,
    },
  });
  return data;
}

export async function listOrderStates(kind?: OrderKind): Promise<RefName[]> {
  // Справочники состояний у заказа покупателя и заказа на перемещение РАЗНЫЕ —
  // без вида фильтр предлагал бы состояния чужого документа.
  const { data } = await api.get<RefName[]>("/order-check/order-states", {
    params: { kind: kind || undefined },
  });
  return data;
}

export async function listOrderOrganizations(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/order-check/organizations", {
    params: { q: q || undefined },
  });
  return data;
}

// ── Сессии проверки сборки ────────────────────────────────────────────────

export type SessionStatus = "open" | "completed" | "cancelled";

export interface SessionHeader {
  id: string;
  display_number: number | null;
  order_ref: string;
  order_number: string;
  order_date: string | null;
  organization_name: string | null;
  kontragent_name: string | null;
  status: SessionStatus;
  created_at: string;
  completed_at: string | null;
  expense_ref: string | null;
  expense_number: string | null;
  network: string;
  /** Комментарий заказа, снятый при открытии проверки. */
  comment: string;
}

export interface NeedLine {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  /** Единица строки заказа: коэф>1 — коробовая (учёт/скан в коробах), 1 — базовая (шт). */
  unit_ref: string;
  unit_coef: number;
  /** Имя БАЗОВОЙ единицы номенклатуры из 1С («шт», «упак»). Пусто — единицы у
   *  номенклатуры нет; тогда количество показываем без слова, а не со словом
   *  наугад. Раньше «шт» было зашито, и перчатки, заказанные УПАКОВКАМИ по
   *  сто пар, читались как штуки (боевой случай 26.08.2026, НФНФ-001265). */
  base_unit_name: string;
  /** Характеристика строки помечена на удаление в 1С (прод 02.10.2026,
   *  НФНФ-000640): штрихкоды и остаток — под действующей характеристикой с тем
   *  же названием, собрать строку нельзя, пока заказ не исправят в 1С. */
  characteristic_deleted: boolean;
  /** Количества — в БАЗОВЫХ штуках; для коробовой строки делим на unit_coef. */
  qty_needed: number;
  qty_scanned: number;
  qty_remaining: number;
}

export interface ScannedExtra {
  nomenclature_ref: string;
  nomenclature_name: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  unit_coef: number;
  qty_scanned: number;
}

export interface PalletSummary {
  id: string;
  position: number;
  label: string | null;
  status: string;
  items_count: number;
  qty: number;
  boxes: number;
  /** Номер документа 1С — у плановой паллеты (план из 1С); у своей пусто. */
  one_c_number: string | null;
  /** Наш SSCC своей паллеты (грузоместо реестра) — он на этикетке. */
  sscc?: string | null;
  /** Архивный номер 1С (`rs_Паллеты`), если он у паллеты есть. */
  archive_number?: string | null;
  /** Паллета уже отгружена (перепроверка заказа) — только смотреть. */
  shipped?: boolean;
  /** Можно ли писать в паллету этой сессией. `false` — отгружена, аннулирована,
   *  перешла к более новой проверке заказа или сессия завершена: бэкенд отобьёт
   *  любую запись (`cargo.assert_writable`). Экран такую паллету только показывает. */
  writable: boolean;
  /** Почему писать нельзя — готовый русский текст отказа бэкенда; при `writable` — null. */
  lock_reason: string | null;
  /** Когда печатали этикетку (null — ещё не клеили). */
  printed_at?: string | null;
  /** Сколько фото снято на паллету (нужно ≥1 на каждую непустую). */
  photos?: number;
  /** Паллету завёл бот-распределитель: не удаляется, собирается строго по плану. */
  is_planned?: boolean;
  /** План паллеты — пусто в свободном режиме (опт). */
  plan?: PlanLine[];
  /** Коробки по плану (короба маркетплейса — Ozon `cargo_id`): только у такой
   *  паллеты, иначе null. Коробка ложится на неё, лишь заняв свободный короб
   *  плана того же состава; россыпь — отказ; завершение ждёт все короба. */
  box_plan?: BoxPlanView | null;
  items: ItemView[];
}

/** Строка состава короба плана. */
export interface BoxPlanLine {
  nomenclature_ref: string;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_name: string | null;
  partia_name: string | null;
  qty: number;
  /** Готовый текст состава с сервера — клиент его не собирает. */
  text: string;
}

/** Ещё не собранные короба плана одного состава. */
export interface BoxPlanGroup {
  lines: BoxPlanLine[];
  count: number;
  /** Ожидаемые коды коробов (`cargo_id` Ozon). */
  markings: string[];
}

/** Коробки по плану паллеты: сколько ждут, сколько собрано, чего не хватает. */
export interface BoxPlanView {
  planned: number;
  /** Короба плана, занятые коробками на этой паллете. */
  filled: number;
  /** Коробок на паллете всего. */
  on_pallet: number;
  free: BoxPlanGroup[];
  /** Коробки на паллете не по плану — словами. */
  extra: string[];
  /** Россыпь на паллете (строк): блокирует завершение, снять можно только коробками по плану. */
  loose: number;
}

/** Строка плана паллеты (директивный режим): что и сколько на неё положить. */
export interface PlanLine {
  line_number: number;
  nomenclature_ref: string;
  nomenclature_name: string | null;
  /** Артикул и имя характеристики бэк подставляет из потребностей заказа:
   *  у строки плана их нет, а подписываться товар обязан одинаково в обеих
   *  шторках. Пусто — номенклатуры нет в заказе (бот положил лишнее). */
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  qty_plan: number;
  qty_scanned: number;
}

/** Собранное, что не помещается в обновлённый план (после правки заказа). */
export interface PlanConflict {
  pallet_label: string;
  nomenclature_name: string;
  partia_name: string | null;
  qty_scanned: number;
  qty_plan: number;
}

export interface SessionDetail {
  header: SessionHeader;
  needs: NeedLine[];
  extras: ScannedExtra[];
  pallets: PalletSummary[];
  /** Сборка строго по плану паллет из 1С (маркетплейсы). */
  is_directive: boolean;
  plan_conflicts: PlanConflict[];
  /** План паллет не сходится с заказом: собрать нельзя ни так, ни иначе. */
  plan_coverage: string[];
  qty_needed_total: number;
  qty_scanned_total: number;
}

/** Перечитать план паллет из 1С — после того, как старший склада поправил заказ. */
export async function reloadPlan(sessionId: string): Promise<SessionDetail> {
  const { data } = await api.post<SessionDetail>(
    `/order-check/sessions/${sessionId}/plan/reload`,
  );
  return data;
}

/** Открыть проверку заказа: продолжить существующую (открытую или
 *  завершённую) или завести первую. «Новой проверки» завершённого заказа нет
 *  (`force_new` убран 27.10.2026, бэкенд отвечает на него 422). */
export async function createOrderCheckSession(
  order_ref: string,
  kind: OrderKind = "customer",
): Promise<SessionHeader> {
  const { data } = await api.post<SessionHeader>("/order-check/sessions", {
    order_ref,
    kind,
  });
  return data;
}

export async function getOrderCheckSession(id: string): Promise<SessionDetail> {
  const { data } = await api.get<SessionDetail>(`/order-check/sessions/${id}`);
  return data;
}

export async function completeOrderCheckSession(id: string): Promise<SessionHeader> {
  const { data } = await api.post<SessionHeader>(`/order-check/sessions/${id}/complete`);
  return data;
}

export async function reopenOrderCheckSession(
  id: string,
  clearScans = false,
): Promise<SessionHeader> {
  const { data } = await api.post<SessionHeader>(`/order-check/sessions/${id}/reopen`, {
    clear_scans: clearScans,
  });
  return data;
}

// ── Сканирование: резолв / партия / паллеты / позиции ─────────────────────

/** `sscc` — коробка портала: её состав знает не 1С, а мы. */
export type ScanKind = "ean13" | "gtin" | "datamatrix" | "gs1" | "sscc";

export interface ResolveRequest {
  scan_kind: ScanKind;
  raw?: string;
  ean13?: string;
  gtin?: string;
  serial?: string;
  batch?: string;
  qty?: number | null;
}

export interface CharOption {
  ref: string;
  name: string;
}

export interface ResolvedScan {
  scan_kind: string;
  matched: boolean;
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  partia_source: string;
  unit_ref: string;
  unit_coefficient: number;
  qty_scanned: number;
  qty_base: number;
  needs_characteristic: boolean;
  needs_partia: boolean;
  available_characteristics: CharOption[];
  message: string;
  /** Предупреждение к успешному скану: вскрытый короб упаковки — «Грузоместо
   *  неполное: было N, осталось M». Экран показывает его тостом. */
  notice?: string;
  /** `packing` — SSCC короба или код паллеты с упаковки: «Принять остаток»
   *  россыпью для него не предлагается (`lib/scan-ui/loose-remainder.tsx`). */
  source?: string;
  /** Сборная коробка портала (сборка и перемещения): её номер и строки состава —
   *  по позиции на строку (`unit_coefficient = qty_base`, `qty_scanned = 1`).
   *  Номенклатуры у такого ответа нет; экран решает по каждой строке
   *  (`lib/order-check.ts::decideBox`) и пишет коробку целиком (`addBoxItems`).
   *  Пусто/нет — обычный скан (у предзагруженного индекса скана поля нет). */
  sscc?: string;
  box_lines?: ResolvedScan[];
}

export interface PartiaOut {
  ref: string;
  name: string;
  expires_at: string | null;
  production_date: string | null;
}

export interface ItemView {
  id: string;
  pallet_id: string;
  scan_kind: string;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  partia_source: string | null;
  qty_scanned: number;
  qty_base: number;
  unit_coefficient: number;
  boxes: number;
  created_at: string;
  /** Код штуки ЧЗ в нормальной форме (без криптохвоста); не штука — пусто.
   *  По нему экран отказывает в повторе (`lib/scan-ui/marks.ts`). */
  mark?: string;  /** SSCC коробки, если позиция записана её кодом (18 цифр, бэк разбирает и AI 00);
   *  иначе пусто. По нему экран отказывает в повторе коробки (`decideBox`). */
  sscc?: string;
}

export interface ItemCreate {
  pallet_id: string;
  scan_kind: string;
  raw?: string;
  ean13?: string | null;
  gtin?: string | null;
  serial?: string | null;
  nomenclature_ref: string;
  nomenclature_name?: string;
  nomenclature_article?: string;
  characteristic_ref?: string;
  characteristic_name?: string;
  partia_ref?: string;
  partia_name?: string;
  partia_source?: string;
  unit_ref?: string;
  unit_coefficient?: number;
  qty_scanned?: number;
  qty_base?: number;
  /** Количество задано человеком (степпер, «принять остаток»), а не взято из
   * кода — журнал отгрузки покажет его как ручное (`qty_source=manual_qty`). */
  qty_manual?: boolean;
}

export async function resolveScan(
  sessionId: string,
  body: ResolveRequest,
): Promise<ResolvedScan> {
  const { data } = await api.post<ResolvedScan>(
    `/order-check/sessions/${sessionId}/resolve`,
    body,
  );
  return data;
}

export interface ScanIndex {
  entries: Record<string, ResolvedScan>;
  partias: Record<string, RefName[]>;
}

export async function getScanIndex(sessionId: string): Promise<ScanIndex> {
  const { data } = await api.get<ScanIndex>(
    `/order-check/sessions/${sessionId}/scan-index`,
  );
  return data;
}

export async function lookupPartia(
  sessionId: string,
  nomenclatureRef: string,
  name: string,
): Promise<PartiaOut> {
  const { data } = await api.get<PartiaOut>(
    `/order-check/sessions/${sessionId}/partia`,
    { params: { nomenclature_ref: nomenclatureRef, name } },
  );
  return data;
}

export async function addPallet(sessionId: string): Promise<PalletSummary> {
  const { data } = await api.post<PalletSummary>(
    `/order-check/sessions/${sessionId}/pallets`,
  );
  return data;
}

/** Напечатать этикетку паллеты (ШК со сквозным номером 1С). Повторно — можно. */
/** Пачка этикеток всех непустых паллет проверки: только здесь на этикетке
 *  печатается «N из M» — во время сборки M ещё неизвестно. */
export async function printAllPalletLabels(
  sessionId: string,
): Promise<{ printed: number; total: number }> {
  const { data } = await api.post<{ printed: number; total: number }>(
    `/order-check/sessions/${sessionId}/pallets/labels`,
  );
  return data;
}

export async function printPalletLabel(sessionId: string, palletId: string): Promise<void> {
  await api.post(`/order-check/sessions/${sessionId}/pallets/${palletId}/label`);
}

export async function deletePallet(sessionId: string, palletId: string): Promise<void> {
  await api.delete(`/order-check/sessions/${sessionId}/pallets/${palletId}`);
}

export async function addItem(sessionId: string, body: ItemCreate): Promise<ItemView> {
  const { data } = await api.post<ItemView>(
    `/order-check/sessions/${sessionId}/items`,
    body,
  );
  return data;
}

/** Сборная коробка на паллету ЦЕЛИКОМ: все строки одной записью или ни одной
 *  (бэк сверяет их с составом коробки сам; отказ — 409 с причиной). */
export interface BoxItemsCreate {
  pallet_id: string;
  raw: string;
  items: ItemCreate[];
}

export async function addBoxItems(sessionId: string, body: BoxItemsCreate): Promise<ItemView[]> {
  const { data } = await api.post<ItemView[]>(
    `/order-check/sessions/${sessionId}/items/box`,
    body,
  );
  return data;
}

/** Ответ удаления скана. `removed_item_ids` — ВСЕ строки, которые сервер снял:
 *  у сборной коробки это все её строки, а не только запрошенная. */
export interface DeleteItemResult {
  removed_item_ids: string[];
}

export async function deleteItem(sessionId: string, itemId: string): Promise<DeleteItemResult> {
  const { data } = await api.delete<DeleteItemResult>(`/order-check/sessions/${sessionId}/items/${itemId}`);
  // Без списка снятых строк массовое удаление не знает, что пропустить, —
  // угадывать по SSCC нельзя (решение по ревью этапа 3б): громко, а не молча.
  if (!Array.isArray(data?.removed_item_ids)) {
    throw new Error("Ответ удаления позиции без removed_item_ids — бэкенд старее фронта");
  }
  return data;
}

export async function moveItem(
  sessionId: string,
  itemId: string,
  palletId: string,
): Promise<ItemView> {
  const { data } = await api.patch<ItemView>(
    `/order-check/sessions/${sessionId}/items/${itemId}`,
    { pallet_id: palletId },
  );
  return data;
}

export interface PalletDoc {
  pallet_id: string;
  label: string;
  ref: string;
  number: string;
}

export interface FinalizeResult {
  expense_ref: string;
  expense_number: string;
  pallets: PalletDoc[];
  extras_count: number;
  photos_attached: number;
  attach_warning?: string | null;
  /** Почему УПД не выписался при сборке. Отгрузка попробует ещё раз. */
  invoice_warning?: string;
}

// ── Фото паллет — черновик сессии (в 1С уходят пакетом при завершении) ─────

export interface PalletPhoto {
  id: string;
  pallet_id: string;
  created_at: string;
  uploaded?: boolean;
}

/** Снять фото паллеты → черновик сессии (в 1С не уходит до завершения). */
export async function stagePalletPhoto(
  sessionId: string,
  palletId: string,
  blob: Blob,
  /** Процент отправки — для индикации в камере (см. `PhotoCapture`). */
  onProgress?: (percent: number) => void,
): Promise<PalletPhoto> {
  const form = new FormData();
  form.append("file", blob, "pallet.jpg");
  const { data } = await api.post<PalletPhoto>(
    `/order-check/sessions/${sessionId}/pallets/${palletId}/photos`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      ...uploadProgress(onProgress),
    },
  );
  return data;
}

export async function listPalletPhotos(sessionId: string): Promise<PalletPhoto[]> {
  const { data } = await api.get<PalletPhoto[]>(
    `/order-check/sessions/${sessionId}/photos`,
  );
  return data;
}

/** Загрузить картинку фото-черновика (auth — Bearer, поэтому через axios) →
 *  object URL для <img src>. Вызывающий обязан `URL.revokeObjectURL` при размонтировании. */
export async function fetchPalletPhotoUrl(
  sessionId: string,
  photoId: string,
): Promise<string> {
  const resp = await api.get<Blob>(
    `/order-check/sessions/${sessionId}/photos/${photoId}`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(resp.data);
}

export async function deletePalletPhoto(sessionId: string, photoId: string): Promise<void> {
  await api.delete(`/order-check/sessions/${sessionId}/photos/${photoId}`);
}

/** Пересобрать палетные листы и ПЕРЕСОХРАНИТЬ вложение в заказе 1С (старые
 *  наши листы помечаются на удаление). */
export async function reattachPalletSheets(
  sessionId: string,
): Promise<{ filename: string }> {
  const { data } = await api.post<{ filename: string }>(
    `/order-check/sessions/${sessionId}/sheets/attach`,
  );
  return data;
}

export async function finalizeOrderCheckSession(id: string): Promise<FinalizeResult> {
  const { data } = await api.post<FinalizeResult>(
    `/order-check/sessions/${id}/finalize`,
  );
  return data;
}

export type ScanMethod = "tsd" | "camera" | "both";

export interface OrderCheckConfig {
  pallet_photo_required: boolean;
  /** Настроен ли принтер этикеток паллет (кнопку печати иначе гасим). */
  pallet_printer_ready: boolean;
  scan_method: ScanMethod;
  /** Названы ли склады-фулфилменты: без них переключателя вида нет. */
  fulfilment_enabled: boolean;
}

export async function getOrderCheckConfig(): Promise<OrderCheckConfig> {
  const { data } = await api.get<OrderCheckConfig>("/order-check/config");
  return data;
}

/** Загрузить фото паллеты — бэк прикрепит его к заказу покупателя в 1С. */
export async function uploadPalletPhoto(
  sessionId: string,
  blob: Blob,
  label: string,
): Promise<void> {
  const form = new FormData();
  form.append("file", blob, "pallet.jpg");
  await api.post(`/order-check/sessions/${sessionId}/photo`, form, {
    params: { label },
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function downloadPalletSheet(
  sessionId: string,
  palletId: string,
): Promise<void> {
  const resp = await api.get<Blob>(
    `/order-check/sessions/${sessionId}/pallets/${palletId}/sheet`,
    { responseType: "blob" },
  );
  saveBlobResponse(resp, `pallet_${palletId}.docx`);
}

export async function downloadAllPalletSheets(sessionId: string): Promise<void> {
  const resp = await api.get<Blob>(`/order-check/sessions/${sessionId}/sheets`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, `pallet_sheets_${sessionId}.docx`);
}

/** Реестр отгрузки (XLSX) — только для заказов сети «Золотое Яблоко». */
export async function downloadZyaReestr(sessionId: string): Promise<void> {
  const resp = await api.get<Blob>(`/order-check/sessions/${sessionId}/reestr`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, `reestr_${sessionId}.xlsx`);
}

/** Сборочное задание (DOCX A4) по заказу покупателя — из списка заказов. */
export async function downloadAssemblyTask(
  orderRef: string,
  kind: OrderKind = "customer",
): Promise<void> {
  const resp = await api.get<Blob>(`/order-check/orders/${orderRef}/assembly-task`, {
    params: { kind },
    responseType: "blob",
  });
  saveBlobResponse(resp, "Сборочное_задание.docx");
}

/** Задать количество позиции (N одинаковых коробов/штук при одном скане). */
export async function updateItemQty(
  sessionId: string,
  itemId: string,
  body: { qty_scanned: number; qty_base: number },
): Promise<ItemView> {
  const { data } = await api.patch<ItemView>(
    `/order-check/sessions/${sessionId}/items/${itemId}/quantity`,
    body,
  );
  return data;
}


// --- сверка раскладки паллет с заказом ---------------------------------------

export interface PlanReviewNeed {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  qty_needed: number;
  qty_planned: number;
  /** `разложено − нужно`. 0 — строка сходится. */
  diff: number;
  /** В заказе партии нет, а раскладка по позиции её несёт — считает бэкенд
   *  (`replan.NeedRow.partia_missing`), тем же правилом, что текст расхождения. */
  partia_missing: boolean;
  /** Штук в коробе (`order_check.box_quantum`, HS `marketplaceproducts`);
   *  `null` — неизвестен, причина — в `qty_in_box_issue`. Единицу не подставлять. */
  qty_in_box: number | null;
  qty_in_box_issue: QtyInBoxIssue;
}

/** Почему кванта нет: не заполнен в 1С / разные числа у МП / нет справочника
 *  привязок у инсталляции / значок сборки квант не запрашивает. */
export type QtyInBoxIssue = "" | "not_filled" | "conflict" | "no_catalog" | "not_requested";

export interface PlanReviewLine {
  line_number: number;
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  qty: number;
  boxes: number;
  fact: number;
  in_order: boolean;
  /** Штук в коробе (`order_check.box_quantum`, HS `marketplaceproducts`);
   *  `null` — неизвестен, причина — в `qty_in_box_issue`. Единицу не подставлять. */
  qty_in_box: number | null;
  qty_in_box_issue: QtyInBoxIssue;
  /** Короба не бьются со штуками по кванту — ПРЕДУПРЕЖДЕНИЕ (не расхождение). */
  box_warning: string;
}

export interface PlanReviewPallet {
  /** id планового места живого плана паллет (реестр, этап 3в) — ключ правки. */
  ref: string;
  /** Наш SSCC грузоместа паллеты; "" — грузоместа ещё нет. */
  number: string;
  qty: number;
  boxes: number;
  lines: PlanReviewLine[];
}

export interface PlanReview {
  order_ref: string;
  order_number: string;
  kontragent_name: string;
  organization_name: string;
  ship_date: string;
  needs: PlanReviewNeed[];
  pallets: PlanReviewPallet[];
  problems: string[];
  in_sync: boolean;
  has_plan: boolean;
  editable: boolean;
  locked_reason: string;
  session_id: string | null;
  session_status: string | null;
}

/** Сверка раскладки паллет в 1С (`rs_Паллеты`) с заказом в 1С — для экрана
 *  сборки. База — та, в которой открыта сессия. Ошибка 1С приходит обычной
 *  HTTP-ошибкой: экран показывает её текст, а не «всё сошлось». */
export async function getSessionPlanReview(sessionId: string): Promise<PlanReview> {
  const { data } = await api.get<PlanReview>(`/order-check/sessions/${sessionId}/plan-review`);
  return data;
}
